import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { fetchData } from '../../helpers/apiUtils';
import { KmeLatestResponse, NfaRecommendation } from '../../helpers/nfaMailingData';

interface Props {
  onSelect: (mailing: NfaRecommendation, date: string) => void;
}

function getNextTuesday(): string {
  const now = new Date();
  const day = now.getDay();
  const daysUntilTuesday = day <= 2 ? 2 - day : 9 - day;
  const next = new Date(now);
  next.setDate(now.getDate() + (daysUntilTuesday === 0 ? 7 : daysUntilTuesday));
  const y = next.getFullYear();
  const m = String(next.getMonth() + 1).padStart(2, '0');
  const d = String(next.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const KapitalmarktexpertenTab: FC<Props> = ({ onSelect }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kmeItem, setKmeItem] = useState<KmeLatestResponse | null>(null);
  const [plannedDate, setPlannedDate] = useState(getNextTuesday);

  const loadLatest = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fetchData<KmeLatestResponse>('/api/nfa-mailings/kme/latest');
    if (result.success && result.data) {
      setKmeItem(result.data);
    } else {
      setError(
        result.error || 'RSS-Feed konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadLatest();
  }, [loadLatest]);

  const handleSelect = () => {
    if (!kmeItem) return;

    const mailing: NfaRecommendation = {
      source_type: 'kme',
      source_id: 0,
      webinar_id: null,
      subject: kmeItem.item.title,
      preview_text: kmeItem.item.summary,
      from_name: kmeItem.item.author,
      html_body: kmeItem.item.html_body,
      campaign_name: 'Kapitalmarktexperten Newsletter',
      product_code: null,
      product_name: null,
      concept: null,
      landing_url: null,
      signup_url: null,
      webinar_title: null,
      webinar_date: null,
      rss_guid: kmeItem.item.rss_guid,
      article_url: kmeItem.item.article_url,
      pub_date: kmeItem.item.pub_date,
      performance: null,
    };

    onSelect(mailing, plannedDate);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadLatest}>
          Erneut versuchen
        </Button>
      </Box>
    );
  }

  if (!kmeItem) {
    return <Alert severity="info">Kein Newsletter verfügbar.</Alert>;
  }

  const { item, already_sent } = kmeItem;

  return (
    <Box>
      <Typography variant="subtitle2" mb={2}>
        Aktueller Kapitalmarktexperten Newsletter
      </Typography>

      {already_sent && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Dieser Newsletter wurde bereits über NFA versendet.
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3, maxWidth: 700 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Chip label="KME" size="small" color="info" />
            <Typography variant="caption" color="textSecondary">
              {new Date(item.pub_date).toLocaleDateString('de-DE')}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              — {item.author}
            </Typography>
          </Box>

          <Typography variant="h6" mb={1}>
            {item.title}
          </Typography>

          {item.summary && (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.summary}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <TextField
          label="Geplantes Versanddatum"
          type="date"
          value={plannedDate}
          onChange={(e) => setPlannedDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />

        <Button variant="contained" onClick={handleSelect} disabled={!kmeItem}>
          Vorschau generieren
        </Button>
      </Box>

      <Button variant="text" size="small" startIcon={<RefreshIcon />} onClick={loadLatest}>
        RSS-Feed neu laden
      </Button>
    </Box>
  );
};

export default KapitalmarktexpertenTab;
